// src/app.config.mock.ts
//
// Application config used ONLY during local development with mocked API data.
// Extends the production config — everything works the same except the mock
// interceptor is prepended to the HTTP interceptor chain.
//
// This file is referenced by main.mock.ts and NEVER by main.ts.

import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZonelessChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
    provideRouter,
    withEnabledBlockingInitialNavigation,
    withHashLocation,
    withInMemoryScrolling
} from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';

import { appRoutes } from './app.routes';
import { HttpClientInterceptor } from '@/core/interceptor/http-client.interceptor';
import { MsgService } from '@/core/service/msg.service';
import { UnAuthGuard } from '@/core/configuration/un-auth.guard';
import { SYSTEM_DATE_TIME_FORMAT_TOKEN } from '@/core/util/date-format.token';
import { DEFAULT_SESSION_MONITORING_CONFIG } from '@/core/util/session-monitor.config';
import { DEFAULT_PERMISSION_CONFIG } from '@/core/util/permission.config';
import { DEFAULT_TRANSLATE_CONFIG } from '@/core/util/translate.config';
import { DEFAULT_SYSTEM_DATE_TIME_FORMAT_CONFIG } from '@/core/util/date-format.config';
import { SESSION_MONITORING_TOKEN } from '@/core/util/session-monitor.token';
import { PERMISSION_CONFIG_TOKEN } from '@/core/util/permission.token';
import { TRANSLATE_CONFIG_TOKEN } from '@/core/util/translate.token';
import { MultiTranslateLoader } from '@/core/loaders/multi-translate.loader';
import { AppI18nService } from '@/core/service/app-i18n-service';
import { PAGE_SIZE_CONFIG, PAGE_SIZE_STORE } from '@/core/util/page-size-store.interface';
import { AUTH_SERVICE_TOKEN } from '@/core/util/auth.token';
import { AuthService } from '@/core/service/auth.service';
import { PERMISSION_SERVICE_TOKEN } from '@/core/util/permission-service.token';
import { PermissionService } from '@/core/service/permission.service';
import { TokenRefreshCoordinator } from '@/core/interceptor/token-refresh-coordinator.service';
import { STORAGE_SERVICE_TOKEN } from '@/core/util/storage.token';
import { StorageService } from '@/core/service/storage.service';
import { MockInterceptor } from '@/mock/mock.interceptor';
import { LocalStoragePageSizeStore } from '@/core/mixins/local-storage-page-size.store';
import { PAGE_SIZE_FEATURE_CONFIG } from '@/core/util/page-size.token';

export function initI18n(i18nService: AppI18nService): () => Promise<void> {
    return () => i18nService.initAsync();
}

export const appMockConfig: ApplicationConfig = {
    providers: [
        MsgService,
        UnAuthGuard,
        TokenRefreshCoordinator,
        { provide: AUTH_SERVICE_TOKEN,       useExisting: AuthService },
        { provide: PERMISSION_SERVICE_TOKEN, useExisting: PermissionService },
        { provide: STORAGE_SERVICE_TOKEN,    useClass: StorageService },

        // ── MockInterceptor runs BEFORE HttpClientInterceptor ────────────────
        // It short-circuits matched URLs so they never reach HttpClientInterceptor.
        // Unmatched URLs pass through normally.
        provideHttpClient(
            withFetch(),
            withInterceptors([MockInterceptor, HttpClientInterceptor])
        ),

        { provide: SYSTEM_DATE_TIME_FORMAT_TOKEN, useValue: DEFAULT_SYSTEM_DATE_TIME_FORMAT_CONFIG },
        { provide: SESSION_MONITORING_TOKEN,      useValue: DEFAULT_SESSION_MONITORING_CONFIG },
        { provide: PERMISSION_CONFIG_TOKEN,       useValue: DEFAULT_PERMISSION_CONFIG },
        { provide: TRANSLATE_CONFIG_TOKEN,        useValue: DEFAULT_TRANSLATE_CONFIG },

        LocalStoragePageSizeStore,
        { provide: PAGE_SIZE_CONFIG, useValue: PAGE_SIZE_FEATURE_CONFIG },
        { provide: PAGE_SIZE_STORE, useExisting: LocalStoragePageSizeStore },

        importProvidersFrom(ReactiveFormsModule),
        importProvidersFrom(
            TranslateModule.forRoot({
                loader: {
                    provide:    TranslateLoader,
                    useFactory: (http: HttpClient) => new MultiTranslateLoader(http, ['share']),
                    deps:       [HttpClient]
                }
            })
        ),

        provideRouter(
            appRoutes,
            withHashLocation(),
            withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
            withEnabledBlockingInitialNavigation()
        ),

        provideAnimationsAsync(),
        provideZonelessChangeDetection(),

        {
            provide:    APP_INITIALIZER,
            useFactory: initI18n,
            deps:       [AppI18nService],
            multi:      true
        }
    ]
};
